import { z } from 'zod';

import { IsoDateSchema } from './common.js';

// ─── Enums ────────────────────────────────────────────────────────────────

/** A1 — dietary identity (what the user will eat). */
export const DietaryIdentityEnum = z.enum([
  'omnivore_all',
  'no_beef',
  'no_beef_pork',
  'chicken_fish_egg',
  'pesco_egg',
  'lacto_ovo',
  'lacto_veg',
  'strict_veg',
]);
export type DietaryIdentity = z.infer<typeof DietaryIdentityEnum>;

/**
 * A2 — periodic identity switches.
 *
 * Cut from the wizard in ADR-0021. Enum kept for column read-compat on
 * legacy rows; never written by the slim wizard.
 */
export const SwitchPeriodEnum = z.enum([
  'none',
  'meat_free_weeks',
  'egg_free_weeks',
  'intermittent_fasting',
  'exam_or_travel',
]);
export type SwitchPeriod = z.infer<typeof SwitchPeriodEnum>;

/** B1 — where the user eats most meals. */
export const MealEnvironmentEnum = z.enum(['hostel_mess', 'pg', 'home', 'mixed']);
export type MealEnvironment = z.infer<typeof MealEnvironmentEnum>;

/** B2 — self-assessed cooking comfort. Drives archetype `minCookingComfort` filter. */
export const CookingComfortEnum = z.enum(['none', 'basic', 'intermediate', 'comfortable']);
export type CookingComfort = z.infer<typeof CookingComfortEnum>;

/**
 * B3 — kitchen equipment available to the user.
 *
 * ADR-0021 surfaces only `fridge` + `stove_2burner` in the wizard. The full
 * enum stays so legacy rows seeded with `microwave`, `blender`, etc. still
 * parse on read.
 */
export const KitchenEquipmentEnum = z.enum([
  'stove_1burner',
  'stove_2burner',
  'pressure_cooker',
  'microwave',
  'fridge',
  'mini_fridge',
  'blender',
  'tawa',
  'oven',
]);
export type KitchenEquipment = z.infer<typeof KitchenEquipmentEnum>;

/**
 * C1 — supplements the user is open to using.
 *
 * ADR-0021 surfaces only `whey` + `creatine` in the wizard. Full enum kept
 * for legacy-row read compatibility.
 */
export const SupplementEnum = z.enum([
  'whey',
  'mass_gainer',
  'creatine',
  'multivit',
  'omega3',
  'preworkout',
]);
export type Supplement = z.infer<typeof SupplementEnum>;

/** D1 — primary carb staple (drives meal generation defaults). */
export const CarbStapleEnum = z.enum(['roti', 'rice', 'both', 'oats_poha', 'millet']);
export type CarbStaple = z.infer<typeof CarbStapleEnum>;

/**
 * D2 — spice tolerance.
 *
 * Cut from the wizard in ADR-0021. Enum kept for legacy-row read compat.
 */
export const SpiceToleranceEnum = z.enum(['mild', 'medium', 'spicy', 'very_spicy']);
export type SpiceTolerance = z.infer<typeof SpiceToleranceEnum>;

/**
 * E1 — training time slot.
 *
 * Cut from the wizard in ADR-0021 (no source on Plan/RoutineTemplate to
 * hydrate from). Enum kept for legacy-row read compat; new rows write null.
 */
export const TrainingTimeSlotEnum = z.enum(['morning', 'afternoon', 'evening']);
export type TrainingTimeSlot = z.infer<typeof TrainingTimeSlotEnum>;

/** F1 — pace preference (drives macro targets aggression). */
export const PacePreferenceEnum = z.enum(['aggressive', 'standard', 'conservative']);
export type PacePreference = z.infer<typeof PacePreferenceEnum>;

/**
 * Wizard step keys for the ADR-0021 slim wizard. Three phases (was six).
 *
 * Legacy values (`supplements`, `foods`, `schedule`, `pace`) are no longer
 * accepted on writes; `lifestyle` now spans supplements + foods + lifestyle,
 * and `plan` replaces schedule + pace.
 */
export const DietPreferencesStepEnum = z.enum(['identity', 'lifestyle', 'plan']);
export type DietPreferencesStep = z.infer<typeof DietPreferencesStepEnum>;

// ─── Nested ───────────────────────────────────────────────────────────────

/** Per-allergy/restriction flags + free-form custom entries. */
export const AllergiesSchema = z.object({
  lactose: z.boolean().default(false),
  peanut: z.boolean().default(false),
  egg: z.boolean().default(false),
  gluten: z.boolean().default(false),
  noOnionGarlicRoot: z.boolean().default(false),
  custom: z.array(z.string().min(1).max(80)).max(20).default([]),
});
export type Allergies = z.infer<typeof AllergiesSchema>;

// ─── Main row shape ───────────────────────────────────────────────────────

// All domain fields are optional/nullable: the wizard PATCHes one phase at a
// time, so an in-flight DietProfile may have any subset filled. Service-layer
// validation gates "complete" on completedSteps containing every step in
// DietPreferencesStepEnum (3 entries post ADR-0021).
//
// Cut fields (maxMealMinutes, spiceTolerance, wakeTime, sleepTime,
// trainingTimeSlot, switchPeriods, supplementBudgetInr) stay on the row for
// legacy read compat but are always written null/[] by the slim wizard.
/** Full DietProfile row as returned by GET /v1/diet/preferences. */
export const DietProfileV1Schema = z.object({
  version: z.literal('v1'),
  // Phase A
  dietaryIdentity: DietaryIdentityEnum.nullable(),
  switchPeriods: z.array(SwitchPeriodEnum).default([]),
  allergies: AllergiesSchema.nullable(),
  // Phase B (lifestyle + supplements + foods, merged in ADR-0021)
  mealEnvironment: MealEnvironmentEnum.nullable(),
  cookingComfort: CookingComfortEnum.nullable(),
  maxMealMinutes: z.number().int().min(1).max(240).nullable(),
  kitchenEquipment: z.array(KitchenEquipmentEnum).default([]),
  monthlyFoodBudgetInr: z.number().int().min(0).max(200000).nullable(),
  supplementsOpen: z.boolean().nullable(),
  supplementsAllowed: z.array(SupplementEnum).default([]),
  supplementBudgetInr: z.number().int().min(0).max(50000).nullable(),
  favouriteFoods: z.array(z.string().min(1).max(80)).max(50).default([]),
  dislikedFoods: z.array(z.string().min(1).max(80)).max(50).default([]),
  carbStaple: CarbStapleEnum.nullable(),
  spiceTolerance: SpiceToleranceEnum.nullable(),
  // Phase C (schedule + pace, merged into 'plan' in ADR-0021)
  mealsPerDay: z.number().int().min(2).max(8).nullable(),
  wakeTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM (24-hour)')
    .nullable(),
  sleepTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM (24-hour)')
    .nullable(),
  trainingTimeSlot: TrainingTimeSlotEnum.nullable(),
  pacePreference: PacePreferenceEnum.nullable(),
  // Wizard state
  completedSteps: z.array(DietPreferencesStepEnum).default([]),
  completedAt: IsoDateSchema.nullable(),
});
export type DietProfileV1 = z.infer<typeof DietProfileV1Schema>;

// ─── PATCH request DTOs (one per wizard phase) ────────────────────────────

/** PATCH body for Phase A — identity + allergies. */
export const UpdateIdentityRequestSchema = z.object({
  dietaryIdentity: DietaryIdentityEnum,
  allergies: AllergiesSchema,
});
export type UpdateIdentityRequest = z.infer<typeof UpdateIdentityRequestSchema>;

/**
 * PATCH body for Phase B — merged lifestyle + supplements + foods (ADR-0021).
 *
 * Cut fields removed: `maxMealMinutes`, `spiceTolerance`,
 * `supplementBudgetInr`. The merged endpoint writes them as null/0 server-side
 * (see DietPreferencesService.updateLifestyle).
 */
export const UpdateLifestyleRequestSchema = z.object({
  mealEnvironment: MealEnvironmentEnum,
  cookingComfort: CookingComfortEnum,
  kitchenEquipment: z.array(KitchenEquipmentEnum).default([]),
  monthlyFoodBudgetInr: z.number().int().min(0).max(200000),
  supplementsOpen: z.boolean(),
  supplementsAllowed: z.array(SupplementEnum).default([]),
  favouriteFoods: z.array(z.string().min(1).max(80)).max(50).default([]),
  dislikedFoods: z.array(z.string().min(1).max(80)).max(50).default([]),
  carbStaple: CarbStapleEnum,
});
export type UpdateLifestyleRequest = z.infer<typeof UpdateLifestyleRequestSchema>;

/**
 * PATCH body for Phase C — meals per day + pace (ADR-0021).
 *
 * Cut fields removed: `wakeTime`, `sleepTime`, `trainingTimeSlot`. Server
 * writes them as null.
 */
export const UpdatePlanRequestSchema = z.object({
  mealsPerDay: z.number().int().min(2).max(8),
  pacePreference: PacePreferenceEnum,
});
export type UpdatePlanRequest = z.infer<typeof UpdatePlanRequestSchema>;

// ─── Response DTOs ────────────────────────────────────────────────────────

/** GET /v1/diet/preferences — profile is null if user hasn't started the wizard. */
export const GetDietPreferencesResponseSchema = z.object({
  profile: DietProfileV1Schema.nullable(),
});
export type GetDietPreferencesResponse = z.infer<typeof GetDietPreferencesResponseSchema>;

/** PATCH /v1/diet/preferences/<phase> — always returns the merged profile. */
export const UpdateDietPreferencesResponseSchema = z.object({
  profile: DietProfileV1Schema,
});
export type UpdateDietPreferencesResponse = z.infer<typeof UpdateDietPreferencesResponseSchema>;

/** POST /v1/diet/preferences/complete — marks the wizard finished. */
export const CompleteDietPreferencesResponseSchema = z.object({
  profile: DietProfileV1Schema,
});
export type CompleteDietPreferencesResponse = z.infer<typeof CompleteDietPreferencesResponseSchema>;
